import { useCallback, useState } from "react";

import { URLS } from "config/urls";
import { ArrowDropDownIcon, LogoutIcon } from "icons";
import { colors } from "theme/palette";

import Button from "components/Button";
import GroupLabel from "components/ButtonDropdown/GroupLabel";
import Link from "components/Link";
import Menu, { MenuItem } from "components/Menu";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import UserAvatar from "components/UserAvatar";
import VisibleIfSuperuser from "components/VisibleIfSuperuser";

import { logout } from "entities/Auth/sdk";
import { useSignedUser } from "entities/Auth/sdk";

const AvatarDropdownButton = () => {
  const { data: user } = useSignedUser();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback(
    (event: any) => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  if (!user) {
    return (
      <Button
        variant="text"
        data-testid="user-dropdown-menu-loading"
        sx={{ "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.11)" } }}
        startIcon={
          <Skeleton
            variant="circular"
            sx={{
              width: 24,
              height: 24,
              backgroundColor: "rgba(255,255,255,0.11)",
            }}
          />
        }
        endIcon={
          <ArrowDropDownIcon
            color="secondary"
            data-testid="user-dropdown-menu-arrow-button"
          />
        }
      >
        <Skeleton
          variant="text"
          sx={{ width: 100, backgroundColor: "rgba(255, 255, 255, 0.11)" }}
        />
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="text"
        onClick={handleClick}
        data-testid="user-dropdown-menu"
        sx={{ "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.11)" } }}
        startIcon={
          <UserAvatar
            alt=""
            src={user.avatar ? user.avatar : undefined}
            imgix
            imgixWidth={24}
            imgixHeight={24}
            sx={{
              width: 24,
              height: 24,
            }}
            dataTestid={
              user.avatar
                ? "custom-user-dropdown-avatar"
                : "default-user-dropdown-avatar"
            }
          />
        }
        endIcon={
          <ArrowDropDownIcon
            color="secondary"
            data-testid="user-dropdown-menu-arrow-button"
          />
        }
      >
        <Text variant="text4" color={colors.white} sx={{ fontWeight: 500 }}>
          {user.first_name} {user.last_name}
        </Text>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock={true}
        onClick={handleClose}
        sx={{ width: "250px" }}
      >
        <Stack padding={2} direction="row" spacing={1.5}>
          <UserAvatar
            alt=""
            src={user.avatar ? user.avatar : undefined}
            imgix
            imgixWidth={32}
            imgixHeight={32}
            sx={{
              width: 32,
              height: 32,
            }}
          />

          <Stack sx={{ overflow: "hidden" }}>
            <Text variant="text2" fontWeight={700} noWrap>
              {user.first_name} {user.last_name}
            </Text>
            <Text variant="text4" noWrap color={colors.gray60}>
              {user.email}
            </Text>
            <Text variant="text4" fontWeight={700} noWrap>
              {user.company_name}
            </Text>
          </Stack>
        </Stack>

        <GroupLabel label="user account" />
        <Link to={URLS.PROFILE}>
          <MenuItem>
            <Text variant="text2">Account details</Text>
          </MenuItem>
        </Link>
        <MenuItem onClick={logout}>
          <Stack direction="row" alignItems="center" gap={1}>
            <LogoutIcon sx={{ height: "20px", width: "20px" }} />
            <Text variant="text2">Log out</Text>
          </Stack>
        </MenuItem>

        <VisibleIfSuperuser>
          <GroupLabel label="customer success admin" />
          <Link to={URLS.SUPERUSER_DASHBOARD}>
            <MenuItem>
              <Text variant="text2">Superuser dashboard</Text>
            </MenuItem>
          </Link>
          <Link to={URLS.SUPERUSER_CUSTOMER_LIST}>
            <MenuItem>
              <Text variant="text2">Control room</Text>
            </MenuItem>
          </Link>

          <GroupLabel label="quick actions (checklists)" />
          <Link to={URLS.SUPERUSER_CHECKLIST_FILE_IMPORT}>
            <MenuItem>
              <Text variant="text2">Checklist file import</Text>
            </MenuItem>
          </Link>
          <Link to={URLS.SUPERUSER_DATA_TYPES_MIGRATION}>
            <MenuItem>
              <Text variant="text2">Data types migration</Text>
            </MenuItem>
          </Link>
          <Link to={URLS.SUPERUSER_LINKED_FIELDS}>
            <MenuItem>
              <Text variant="text2">Add linked field</Text>
            </MenuItem>
          </Link>
        </VisibleIfSuperuser>
      </Menu>
    </>
  );
};
export default AvatarDropdownButton;
